import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  MapPin,
  Users,
  CheckCircle,
  MessageCircle,
  Globe,
  Wallet,
  BookOpen,
  ArrowRight,
  Star,
  Menu,
  X,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: CheckCircle,
      title: "Smart Checklists",
      description: "Never miss a step with personalized moving checklists for visa, housing, and banking.",
    },
    {
      icon: Globe,
      title: "Country Guides",
      description: "In-depth guides for Ireland, UK, and Canada covering everything you need to know.",
    },
    {
      icon: Wallet,
      title: "Budget Planner",
      description: "Plan your finances with real cost estimates for your destination country.",
    },
    {
      icon: MessageCircle,
      title: "Local Homey Chat",
      description: "Get matched with a local who can answer all your questions about life in your new country.",
    },
    {
      icon: MapPin,
      title: "Local Services",
      description: "Find banks, healthcare, housing, and utilities trusted by expats.",
    },
    {
      icon: BookOpen,
      title: "Cultural Guides",
      description: "Learn local customs, etiquette, and tips to help you fit in faster.",
    },
  ];

  const countries = [
    {
      name: "Ireland",
      flag: "🇮🇪",
      image: "https://images.unsplash.com/photo-1565882135091-8e52d4c81762?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxpcmVsYW5kJTIwZ3JlZW4lMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzcxMTE0MTkyfDA&ixlib=rb-4.1.0&q=85",
      tagline: "Céad míle fáilte",
    },
    {
      name: "United Kingdom",
      flag: "🇬🇧",
      image: "https://images.unsplash.com/photo-1764079648275-a37d1f5104ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwY296eXxlbnwwfHx8fDE3NzExMTQxOTh8MA&ixlib=rb-4.1.0&q=85",
      tagline: "Welcome home",
    },
    {
      name: "Canada",
      flag: "🇨🇦",
      image: "https://images.unsplash.com/photo-1542190914-0771f5603026?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYW5hZGElMjBtb3VudGFpbnMlMjBsYWtlfGVufDB8fHx8MTc3MTExNDIwMHww&ixlib=rb-4.1.0&q=85",
      tagline: "Bienvenue",
    },
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      country: "Moved to Ireland",
      text: "Local Homey made my move to Dublin so much easier. My Local Homey answered all my questions about PPS numbers and finding a GP!",
      avatar: "SM",
    },
    {
      name: "James L.",
      country: "Moved to Canada",
      text: "The budget planner helped me understand real costs in Toronto. The cultural guide was spot-on about Canadian politeness!",
      avatar: "JL",
    },
    {
      name: "Emma K.",
      country: "Moved to UK",
      text: "Being matched with a local in London was invaluable. They helped me navigate everything from banking to understanding British humor.",
      avatar: "EK",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Local Homey</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-[#4A5568] hover:text-[#4A6C6F] transition-colors">Features</a>
              <a href="#countries" className="text-[#4A5568] hover:text-[#4A6C6F] transition-colors">Countries</a>
              <a href="#testimonials" className="text-[#4A5568] hover:text-[#4A6C6F] transition-colors">Stories</a>
              <Button
                data-testid="login-btn-nav"
                onClick={handleLogin}
                className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full px-6"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#E2E8F0]">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-[#4A5568] hover:text-[#4A6C6F] py-2">Features</a>
                <a href="#countries" className="text-[#4A5568] hover:text-[#4A6C6F] py-2">Countries</a>
                <a href="#testimonials" className="text-[#4A5568] hover:text-[#4A6C6F] py-2">Stories</a>
                <Button
                  data-testid="login-btn-mobile"
                  onClick={handleLogin}
                  className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#F5F5F0] rounded-full px-4 py-2">
                <Users className="w-4 h-4 text-[#4A6C6F]" />
                <span className="text-sm text-[#4A5568]">Trusted by 10,000+ expats</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A202C] leading-tight">
                Your guide to feeling <span className="text-[#4A6C6F]">at home</span> in a new country
              </h1>
              
              <p className="text-lg text-[#4A5568] leading-relaxed max-w-xl">
                Moving abroad is exciting but overwhelming. Local Homey connects you with locals, guides you through the process, and helps you settle in faster.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-cta-btn"
                  onClick={handleLogin}
                  className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-shadow"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  data-testid="learn-more-btn"
                  variant="outline"
                  className="border-2 border-[#4A6C6F] text-[#4A6C6F] hover:bg-[#4A6C6F]/5 rounded-full px-8 py-6 text-lg"
                  onClick={() => document.getElementById("features").scrollIntoView({ behavior: "smooth" })}
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1610070835951-156b6921281d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMGRpdmVyc2UlMjBzdHVkZW50cyUyMHRhbGtpbmd8ZW58MHx8fHwxNzcxMTE0MjA3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Diverse group of friends representing community"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4A6C6F]/30 to-transparent"></div>
              </div>
              
              {/* Floating cards */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#48BB78]/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#48BB78]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A202C]">Visa Approved!</p>
                    <p className="text-xs text-[#718096]">Just now</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 animate-float animation-delay-400">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D97757]/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#D97757]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A202C]">New message</p>
                    <p className="text-xs text-[#718096]">From your Local Homey</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A202C] mb-4">
              Everything you need to relocate
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              From planning your move to settling in, we've got you covered with tools and support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={feature.title}
                data-testid={`feature-card-${index}`}
                className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow rounded-2xl overflow-hidden group cursor-pointer"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#4A6C6F]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#4A6C6F]/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-[#4A6C6F]" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#1A202C] mb-2">{feature.title}</h3>
                  <p className="text-[#4A5568]">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section id="countries" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A202C] mb-4">
              Choose your destination
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              We currently support Ireland, UK, and Canada with more countries coming soon.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {countries.map((country, index) => (
              <div
                key={country.name}
                data-testid={`country-card-${country.name.toLowerCase().replace(" ", "-")}`}
                className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group cursor-pointer h-[300px]"
              >
                <img
                  src={country.image}
                  alt={country.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{country.flag}</span>
                    <h3 className="font-heading text-2xl font-semibold">{country.name}</h3>
                  </div>
                  <p className="text-white/80 italic">{country.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Homey Section */}
      <section className="py-16 md:py-24 bg-[#4A6C6F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-white">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold">
                Connect with a Local Homey
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                Get matched with someone who's already living in your destination country. Ask real questions, get authentic answers, and make the transition smoother.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span>Real-time chat with verified locals</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span>Auto-suggested answers for common questions</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span>Country-specific expertise</span>
                </li>
              </ul>
              <Button
                data-testid="become-homey-btn"
                onClick={handleLogin}
                className="bg-white text-[#4A6C6F] hover:bg-white/90 rounded-full px-8 py-6 text-lg mt-4"
              >
                Become a Local Homey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1676844698135-cfed02b5b460?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwZnJpZW5kcyUyMGxhdWdoaW5nJTIwY29mZmVlJTIwc2hvcHxlbnwwfHx8fDE3NzExMTQxNzV8MA&ixlib=rb-4.1.0&q=85"
                alt="Friends in coffee shop"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A202C] mb-4">
              Stories from our community
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Hear from expats who found their footing with Local Homey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={testimonial.name}
                data-testid={`testimonial-${index}`}
                className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D97757] text-[#D97757]" />
                    ))}
                  </div>
                  <p className="text-[#4A5568] mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center text-white font-medium">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-medium text-[#1A202C]">{testimonial.name}</p>
                      <p className="text-sm text-[#718096]">{testimonial.country}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A202C] mb-6">
            Ready to make your move?
          </h2>
          <p className="text-lg text-[#4A5568] mb-8 max-w-2xl mx-auto">
            Join thousands of expats who've made their transition smoother with Local Homey. Sign up now and start your journey.
          </p>
          <Button
            data-testid="final-cta-btn"
            onClick={handleLogin}
            className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-shadow"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A202C] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading text-xl font-semibold">Local Homey</span>
              </div>
              <p className="text-[#A0AEC0] text-sm">
                Helping expats, students, and digital nomads find their home away from home.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Countries</h4>
              <ul className="space-y-2 text-[#A0AEC0] text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Ireland</a></li>
                <li><a href="#" className="hover:text-white transition-colors">United Kingdom</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Canada</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-[#A0AEC0] text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Country Guides</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cultural Tips</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Budget Planner</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Community</h4>
              <ul className="space-y-2 text-[#A0AEC0] text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Become a Local Homey</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Success Stories</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#2D3748] mt-8 pt-8 text-center text-[#A0AEC0] text-sm">
            <p>&copy; {new Date().getFullYear()} Local Homey. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
